import type { Requirement } from '../auth/permissions';
import type { IconName } from '../ui/icons';

export interface NavItem extends Requirement {
  to: string;
  label: string;
  icon: IconName;
}

export interface NavGroup {
  title: string;
  icon: IconName;
  items: NavItem[];
}

/**
 * Menú lateral. Cada ítem declara el permiso y la funcionalidad del plan que requiere; la misma
 * regla protege la pantalla aunque se entre por la dirección (ver `navRequirement`).
 * `sales` = catálogo, cotizaciones, pedidos y pagos (los 3 productos); `erp` = inventario,
 * compras, facturas y finanzas (ERP y Sales).
 */
export const NAVIGATION: NavGroup[] = [
  {
    title: 'General',
    icon: 'home',
    items: [
      { to: '/', icon: 'home', label: 'Tablero', feature: 'sales' },
      {
        to: '/reports',
        feature: 'sales',
        icon: 'chart',
        label: 'Reportes',
        anyPerm: ['reports.sales', 'reports.inventory', 'reports.finance'],
      },
    ],
  },
  {
    title: 'CRM',
    icon: 'funnel',
    items: [
      { to: '/inbox', icon: 'inbox', label: 'Inbox', perm: 'inbox.read', feature: 'inbox' },
      { to: '/crm', icon: 'funnel', label: 'Embudo de ventas', perm: 'crm.read', feature: 'crm' },
      {
        to: '/crm/activities',
        icon: 'check',
        label: 'Pendientes',
        perm: 'crm.read',
        feature: 'crm',
      },
      {
        to: '/crm/calendar',
        icon: 'calendar',
        label: 'Calendario',
        perm: 'crm.read',
        feature: 'crm',
      },
      {
        to: '/crm/team',
        icon: 'users',
        label: 'Equipo comercial',
        perm: 'crm.read',
        feature: 'crm',
      },
      {
        // Clasifican las oportunidades y fijan el margen mínimo de las propuestas.
        to: '/crm/business-types',
        icon: 'tag',
        label: 'Tipos de negocio',
        perm: 'tenant.manage',
        anyFeature: ['crm', 'services'],
      },
    ],
  },
  {
    // La sección viene en CRM y Sales, con un cupo de conversaciones del agente en cada plan.
    title: 'Agente IA',
    icon: 'bot',
    items: [
      {
        to: '/ai-agent',
        icon: 'bot',
        label: 'Mi agente',
        perm: 'integrations.manage',
        feature: 'ai_agent',
      },
      {
        to: '/ai-agent/settings',
        icon: 'sliders',
        label: 'Ajustes del agente',
        perm: 'integrations.manage',
        feature: 'ai_agent',
      },
      {
        to: '/ai-agent/catalog',
        icon: 'box',
        label: 'Catálogo del agente',
        perm: 'catalog.read',
        feature: 'ai_agent',
      },
      {
        to: '/ai-agent/plans',
        icon: 'gauge',
        label: 'Conversaciones del agente',
        perm: 'billing.read',
        feature: 'ai_agent',
      },
    ],
  },
  {
    title: 'Ventas',
    icon: 'cart',
    items: [
      { to: '/sales/orders', icon: 'cart', label: 'Pedidos', perm: 'sales.read', feature: 'sales' },
      {
        to: '/sales/invoices',
        icon: 'file',
        label: 'Facturas',
        perm: 'invoices.read',
        feature: 'erp',
      },
      {
        to: '/sales/payments',
        icon: 'card',
        label: 'Pagos',
        perm: 'payments.read',
        feature: 'sales',
      },
      {
        to: '/sales/receipts',
        icon: 'receipt',
        label: 'Recibos de caja',
        perm: 'payments.read',
        feature: 'erp',
      },
      {
        to: '/sales/returns',
        icon: 'undo',
        label: 'Devoluciones',
        perm: 'sales.read',
        feature: 'erp',
      },
      { to: '/customers', icon: 'users', label: 'Clientes', perm: 'customers.read' },
      { to: '/contacts', icon: 'user', label: 'Contactos', perm: 'customers.read' },
      { to: '/sales/regions', icon: 'pin', label: 'Regiones', perm: 'tenant.manage' },
    ],
  },
  {
    title: 'Servicios a la medida',
    icon: 'layers',
    items: [
      {
        to: '/services/proposals',
        icon: 'file',
        label: 'Propuestas',
        perm: 'services.read',
        feature: 'services',
      },
      {
        to: '/services/projects',
        icon: 'clipboard',
        label: 'Proyectos',
        perm: 'services.read',
        feature: 'services',
      },
      {
        to: '/services/time',
        icon: 'history',
        label: 'Mis horas',
        perm: 'time.register',
        feature: 'services',
      },
      {
        to: '/services/reports',
        icon: 'chart',
        label: 'Informes de servicios',
        perm: 'services.read',
        feature: 'services',
      },
      { to: '/assistant', icon: 'bot', label: 'Asistente', feature: 'ai_assistant' },
      {
        to: '/services/settings',
        icon: 'sliders',
        label: 'Perfiles y ajustes',
        perm: 'services.read',
        feature: 'services',
      },
    ],
  },
  {
    title: 'Catálogo',
    icon: 'box',
    items: [
      {
        to: '/catalog/products',
        icon: 'box',
        label: 'Productos y servicios',
        perm: 'catalog.read',
        feature: 'sales',
      },
      {
        to: '/catalog/status-shares',
        icon: 'send',
        label: 'Estados de WhatsApp',
        perm: 'catalog.read',
        feature: 'sales',
      },
      {
        to: '/catalog/setup',
        icon: 'tag',
        label: 'Categorías e impuestos',
        perm: 'catalog.read',
        feature: 'sales',
      },
      {
        to: '/catalog/price-lists',
        icon: 'list',
        label: 'Listas de precios',
        perm: 'catalog.read',
        feature: ['sales', 'price_lists'],
      },
    ],
  },
  {
    title: 'Inventario',
    icon: 'layers',
    items: [
      {
        to: '/inventory/stock',
        icon: 'layers',
        label: 'Existencias',
        perm: 'inventory.read',
        feature: 'erp',
      },
      {
        to: '/inventory/movements',
        icon: 'history',
        label: 'Kardex',
        perm: 'inventory.read',
        feature: 'erp',
      },
      {
        to: '/inventory/adjust',
        feature: 'erp',
        icon: 'sliders',
        label: 'Ajustar inventario',
        perm: 'inventory.adjust',
      },
      {
        to: '/inventory/transfer',
        icon: 'transfer',
        label: 'Transferir',
        perm: 'inventory.transfer',
        feature: ['erp', 'multi_warehouse'],
      },
      {
        to: '/inventory/counts',
        feature: 'erp',
        icon: 'clipboard',
        label: 'Tomas físicas',
        perm: 'inventory.read',
      },
      {
        to: '/inventory/warehouses',
        icon: 'warehouse',
        label: 'Bodegas',
        perm: 'inventory.read',
        feature: 'erp',
      },
      {
        to: '/inventory/locations',
        icon: 'pin',
        label: 'Ubicaciones',
        perm: 'locations.read',
        feature: 'warehouse_locations',
      },
      {
        to: '/inventory/plan',
        icon: 'plan',
        label: 'Plano de bodega',
        perm: 'locations.read',
        feature: 'warehouse_locations',
      },
      {
        // Inicio para el personal de bodega en el celular (buscar, escanear, recibir, alistar).
        to: '/bodega',
        icon: 'home',
        label: 'Inicio de bodega',
        perm: 'locations.read',
        feature: 'warehouse_locations',
      },
    ],
  },
  {
    title: 'Compras',
    icon: 'truck',
    items: [
      {
        to: '/purchasing/orders',
        icon: 'truck',
        label: 'Órdenes de compra',
        perm: 'purchasing.read',
        feature: ['erp', 'purchasing'],
      },
      {
        to: '/suppliers',
        icon: 'building',
        label: 'Proveedores',
        perm: 'suppliers.read',
        feature: 'erp',
      },
    ],
  },
  {
    title: 'Finanzas',
    icon: 'bank',
    items: [
      {
        to: '/treasury',
        icon: 'bank',
        label: 'Cajas y bancos',
        perm: 'treasury.read',
        feature: 'erp',
      },
      { to: '/expenses', icon: 'wallet', label: 'Gastos', perm: 'expenses.read', feature: 'erp' },
      {
        to: '/accounting',
        icon: 'book',
        label: 'Contabilidad',
        perm: 'accounting.read',
        feature: ['erp', 'accounting'],
      },
      {
        to: '/reports/tax',
        icon: 'percent',
        label: 'IVA y exógena',
        perm: 'reports.finance',
        feature: 'erp',
      },
    ],
  },
  {
    title: 'Configuración',
    icon: 'settings',
    items: [
      { to: '/settings/company', icon: 'company', label: 'Empresa', perm: 'tenant.read' },
      {
        to: '/settings/integrations',
        icon: 'plug',
        label: 'Integraciones',
        perm: 'integrations.manage',
      },
      { to: '/settings/team', icon: 'users', label: 'Equipo', perm: 'users.read' },
      { to: '/settings/roles', icon: 'shield', label: 'Roles y permisos', perm: 'roles.read' },
      { to: '/settings/security', icon: 'lock', label: 'Mi seguridad' },
      { to: '/settings/notifications', icon: 'bell', label: 'Notificaciones' },
      { to: '/settings/data', icon: 'swap', label: 'Importar y exportar' },
      {
        to: '/settings/label-formats',
        icon: 'tag',
        label: 'Formatos de etiqueta',
        perm: 'locations.read',
        feature: 'warehouse_locations',
      },
      { to: '/settings/billing', icon: 'gauge', label: 'Plan y consumo', perm: 'billing.read' },
      {
        to: '/settings/gateways',
        feature: ['sales', 'payment_links'],
        icon: 'card',
        label: 'Pasarelas de pago',
        perm: 'integrations.manage',
      },
      {
        to: '/settings/einvoicing',
        icon: 'fileCheck',
        label: 'Facturación electrónica',
        perm: 'integrations.manage',
        feature: ['erp', 'einvoicing'],
      },
      {
        to: '/settings/api-keys',
        icon: 'key',
        label: 'API keys',
        perm: 'api_keys.manage',
        feature: 'api_access',
      },
      {
        to: '/settings/channels',
        icon: 'chat',
        label: 'Canales (WhatsApp)',
        perm: 'inbox.read',
        feature: 'inbox',
      },
      {
        to: '/settings/webhooks',
        icon: 'link',
        label: 'Avisos automáticos',
        perm: 'webhooks.manage',
        feature: 'webhooks',
      },
      {
        to: '/settings/audit',
        icon: 'activity',
        label: 'Auditoría',
        perm: 'audit.read',
        feature: 'audit_log',
      },
    ],
  },
  {
    title: 'Ayuda',
    icon: 'help',
    items: [
      { to: '/help', icon: 'help', label: 'Centro de ayuda' },
      { to: '/support', icon: 'lifebuoy', label: 'Soporte' },
    ],
  },
  {
    title: 'Plataforma',
    icon: 'server',
    items: [
      { to: '/platform/tenants', icon: 'building', label: 'Empresas', platformAdmin: true },
      {
        to: '/platform/agent-requests',
        icon: 'bot',
        label: 'Solicitudes de agente',
        platformAdmin: true,
      },
      { to: '/platform/support', icon: 'lifebuoy', label: 'Soporte', platformAdmin: true },
      {
        to: '/platform/plans',
        icon: 'layers',
        label: 'Productos y planes',
        platformAdmin: true,
      },
      { to: '/platform/addons', icon: 'box', label: 'Complementos', platformAdmin: true },
      { to: '/platform/metrics', icon: 'chart', label: 'Métricas', platformAdmin: true },
      { to: '/platform/integrations', icon: 'plug', label: 'Integraciones', platformAdmin: true },
      {
        to: '/platform/email-templates',
        icon: 'mail',
        label: 'Plantillas de correo',
        platformAdmin: true,
      },
      {
        to: '/platform/admins',
        icon: 'shield',
        label: 'Super administradores',
        platformAdmin: true,
      },
    ],
  },
];

/**
 * Requisito de la pantalla en `path`: el del ítem del menú más específico que la contiene
 * (`/sales/orders/123` hereda el de «Pedidos»). Null si ninguna entrada del menú la cubre.
 */
export function navRequirement(path: string): NavItem | null {
  let best: NavItem | null = null;
  for (const group of NAVIGATION)
    for (const item of group.items) {
      if (item.to === '/') continue;
      const covers = path === item.to || path.startsWith(`${item.to}/`);
      if (covers && (!best || item.to.length > best.to.length)) best = item;
    }
  return best;
}
